import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, User, EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function Login({ setIsAuthenticated }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const response = await axios.post(`${API_URL}${endpoint}`, {
        username,
        password,
      });

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('username', response.data.username);
      localStorage.setItem('role', response.data.role || 'admin');
      if (response.data.service_id) {
        localStorage.setItem('user_service_id', response.data.service_id);
        localStorage.setItem('user_service_name', response.data.service_name || '');
        localStorage.setItem('service_filter', response.data.service_id);
      } else {
        localStorage.removeItem('user_service_id');
        localStorage.removeItem('user_service_name');
        localStorage.setItem('service_filter', 'all');
      }
      setIsAuthenticated(true);
      const role = response.data.role || 'admin';
      if (role === 'face_only' || role === 'attendance_only') {
        navigate('/mark-attendance');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`);
      if (response.data.success) {
        toast.success('Credentials list sent to admin email');
      } else {
        toast.info(response.data.message);
      }
    } catch (err) {
      toast.error('Failed to send forgot password email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_face-check-dance/artifacts/lstu64pj_MRVA%20logo%20final%20white.jpg)',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      
      <div className="w-full max-w-md relative z-10">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-8 shadow-2xl">
          <div className="text-center mb-8">
            <img src="/mrva-logo.png" alt="MRVA Productions" className="h-16 mx-auto mb-2 object-contain" />
            <p className="text-xs text-slate-500 mt-3">Attendance Management System</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                Username
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500" size={20} weight="duotone" />
                <input
                  type="text"
                  data-testid="username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-md pl-11 pr-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-slate-600"
                  placeholder="Enter username"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-blue-500 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500" size={20} weight="duotone" />
                <input
                  type="password"
                  data-testid="password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-md pl-11 pr-4 py-3 text-slate-50 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 placeholder-slate-600"
                  placeholder="Enter password"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="bg-rose-900/20 border border-rose-800 rounded-md p-3 text-rose-400 text-sm" data-testid="error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium px-6 py-3 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : (isRegister ? 'Register' : 'Login')}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-800">
            <button
              onClick={handleForgotPassword}
              data-testid="forgot-password-button"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 text-slate-400 hover:text-slate-300 text-sm transition-colors disabled:opacity-50"
            >
              <EnvelopeSimple size={16} weight="duotone" />
              Forgot Password?
            </button>
            <p className="text-xs text-slate-500 text-center mt-2">
              Credentials will be sent to admin email
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
